/**
 * Lightweight media controller
 * Handles play/pause toggling and mute state for any media block video.
 * Videos start muted and autoplayed, but users can toggle mute.
 */
class MediaController {
  constructor(root) {
    this.root = root;
    this.video = root.querySelector('video');
    this.toggle = root.querySelector('[data-media-mute]');
    this.videoContainer = root.querySelector('.media-block__visual');

    if (!this.video) return;

    // For iOS autoplay: ensure video has required attributes
    if (this.video.hasAttribute('autoplay')) {
      // iOS Safari requires these attributes for autoplay to work
      this.video.setAttribute('playsinline', '');
      this.video.setAttribute('muted', '');
      this.video.muted = true;
      this.video.setAttribute('preload', 'auto');
      
      // iOS Safari requires user interaction for autoplay
      // Track if we've had user interaction and if video is ready
      let hasUserInteracted = false;
      let videoReady = false;
      
      const tryAutoplay = () => {
        // Only try if we have user interaction AND video is ready
        if (!hasUserInteracted || !videoReady || !this.video.paused) return;
        
        const playPromise = this.video.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // Successfully started playing
            })
            .catch(() => {
              // Autoplay was prevented - this can happen on iOS
              // Will retry on next interaction
            });
        }
      };

      // Mark video as ready when it can play
      const markVideoReady = () => {
        if (this.video.readyState >= 2) {
          videoReady = true;
          tryAutoplay();
        }
      };

      // Set up video ready listeners
      if (this.video.readyState >= 2) {
        markVideoReady();
      } else {
        this.video.addEventListener('loadeddata', markVideoReady, { once: true });
        this.video.addEventListener('canplay', markVideoReady, { once: true });
        this.video.addEventListener('loadedmetadata', markVideoReady, { once: true });
      }

      // Listen for first user interaction (iOS requirement)
      const interactionEvents = ['touchstart', 'touchend', 'click'];
      const handleFirstInteraction = () => {
        hasUserInteracted = true;
        tryAutoplay();
        // Keep listeners active in case video wasn't ready yet
      };

      interactionEvents.forEach(eventType => {
        document.addEventListener(eventType, handleFirstInteraction, { passive: true });
      });
    }

    if (this.toggle) {
      this.label = this.toggle.querySelector('[data-media-mute-label]') || this.toggle;
      this.muteOnLabel = this.toggle.dataset.muteOn || 'MUTE ON';
      this.muteOffLabel = this.toggle.dataset.muteOff || 'MUTE OFF';

      this.handleVideoClick = this.handleVideoClick.bind(this);
      this.handleToggleClick = this.handleToggleClick.bind(this);
      this.handleVolumeChange = this.handleVolumeChange.bind(this);

      // Attach click handler to both video and container for better UX
      if (this.videoContainer) {
        this.videoContainer.addEventListener('click', this.handleVideoClick);
        this.videoContainer.style.cursor = 'pointer';
      } else {
        this.video.addEventListener('click', this.handleVideoClick);
        this.video.style.cursor = 'pointer';
      }
      this.video.addEventListener('volumechange', this.handleVolumeChange);
      this.toggle.addEventListener('click', this.handleToggleClick);

      this.handleVolumeChange();
    } else {
      this.handleVideoClick = this.handleVideoClick.bind(this);
      // Attach click handler to both video and container for better UX
      if (this.videoContainer) {
        this.videoContainer.addEventListener('click', this.handleVideoClick);
        this.videoContainer.style.cursor = 'pointer';
      } else {
        this.video.addEventListener('click', this.handleVideoClick);
        this.video.style.cursor = 'pointer';
      }
    }
  }

  handleVideoClick(event) {
    // Don't prevent default if clicking on mute button (it has stopPropagation)
    if (event.target.closest('[data-media-mute]')) {
      return;
    }
    
    event.preventDefault();
    event.stopPropagation();

    if (this.video.paused) {
      this.video.play().catch(() => {
        // Ignore play errors (e.g., autoplay restrictions)
      });
    } else {
      this.video.pause();
    }
  }

  handleToggleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    this.video.muted = !this.video.muted;
    this.handleVolumeChange();
  }

  handleVolumeChange() {
    if (!this.toggle) return;

    const isMuted = this.video.muted;
    const nextLabel = isMuted ? this.muteOffLabel : this.muteOnLabel;

    this.label.textContent = nextLabel;
    this.toggle.setAttribute('aria-pressed', (!isMuted).toString());
    this.toggle.dataset.state = isMuted ? 'muted' : 'unmuted';
  }

  destroy() {
    if (this.videoContainer) {
      this.videoContainer.removeEventListener('click', this.handleVideoClick);
    } else {
      this.video.removeEventListener('click', this.handleVideoClick);
    }
    if (this.toggle) {
      this.video.removeEventListener('volumechange', this.handleVolumeChange);
      this.toggle.removeEventListener('click', this.handleToggleClick);
    }
  }
}

const mediaControllers = new WeakMap();

const initMediaControllers = (scope = document) => {
  const players = scope.querySelectorAll('[data-media-player]');

  players.forEach((player) => {
    if (mediaControllers.has(player)) return;
    const controller = new MediaController(player);

    if (controller.video) {
      mediaControllers.set(player, controller);
    }
  });
};

const destroyMediaControllers = (scope = document) => {
  const players = scope.querySelectorAll('[data-media-player]');

  players.forEach((player) => {
    const controller = mediaControllers.get(player);
    if (controller) {
      controller.destroy();
      mediaControllers.delete(player);
    }
  });
};

document.addEventListener('DOMContentLoaded', () => {
  initMediaControllers();
});

document.addEventListener('shopify:section:load', (event) => {
  initMediaControllers(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyMediaControllers(event.target);
});

document.addEventListener('shopify:block:select', (event) => {
  initMediaControllers(event.target);
});

document.addEventListener('shopify:block:deselect', (event) => {
  initMediaControllers(event.target);
});
